import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { GuidanceBlockedReason, GuidanceStep, TechPackGuidanceReport } from "pages/TechPackPage/utils/guidanceReport";

/**
 * The two halves of the guidance runtime, kept in one slice because they only ever mean anything
 * together — and written by different owners on purpose:
 *
 * - `published` — what the feature the user is currently on says about their progress (T1.1).
 *   Written by the feature's provider through `useGuidanceReporter`, and cleared when it unmounts.
 * - `active` — which guide is running (T1.3). Written only by the coordinator.
 *
 * The split is what makes the away state work: the feature clearing `published` says nothing about
 * whether a guide is still running, so navigating off Tech Pack leaves `active` untouched.
 *
 * `published` is deliberately separate from `common.currentPageState`: that snapshot is **chatbot
 * transport** — a flattened, capped, primitive-only projection shipped with `/chatbot/ask`. This is
 * the FE-internal semantic report a guide reads. They are computed from one source in the feature
 * and published to two outlets on purpose; conflating them would make the chatbot's field cap a
 * constraint on the guidance runtime.
 *
 * Also deliberately not on the Tech Pack context: that context is wide and re-renders every
 * consumer, and the guide renderer lives nowhere near the Tech Pack tree.
 *
 * `report` is Tech Pack's shape because Tech Pack is the only guided flow. It becomes a union when
 * a second one is piloted — `feature` is already the discriminator.
 */
export interface PublishedGuidanceReport {
  /** Which feature published it, matching the capability id a flow definition carries. */
  feature: string;
  report: TechPackGuidanceReport;
}

/** How the guide was started. Only the two the pilot needs — `chatbot` is Phase 2's caller. */
export type GuideSource = "url_param" | "chatbot";

/**
 * The one active-guide record. Note what is NOT here: the current step, its readiness, and the
 * blocked reason. Those live in `published`, owned by the feature — storing them here would create
 * a second source of truth that could disagree with the wizard the user is looking at.
 */
export interface ActiveGuide {
  flowId: string;
  /** The definition version this run started against. A restored record from another one is discarded. */
  version: number;
  source: GuideSource;
  /** Epoch ms. The only value in this record that cannot be re-derived — it is `guide_completed`'s duration. */
  startedAt: number;
  status: "active" | "completed";
  /**
   * Analytics bookkeeping ONLY — the last values handed to GA4, never read as "where the user is".
   *
   * They live in the store rather than in a ref because `Layout` remounts on every navigation
   * (`Router.tsx::getRoute`), and a ref would re-emit `guide_step_shown` for the same step after
   * every route change. Keeping them beside the record also makes duplicate emission impossible
   * under StrictMode's double-invoked effects, which read this back through live store state.
   */
  lastEmittedStep: GuidanceStep | null;
  lastEmittedBlockedReason: GuidanceBlockedReason | null;
}

export const guidanceSlice = createSlice({
  name: "guidance",
  initialState: {
    /** Null whenever the user is not on a feature that reports progress. */
    published: null as PublishedGuidanceReport | null,
    /** Null when no guide is running. At most one, ever — this pilot has no notion of nesting. */
    active: null as ActiveGuide | null,
  },
  reducers: {
    setGuidanceReport: (state, action: PayloadAction<PublishedGuidanceReport>) => {
      state.published = action.payload;
    },
    clearGuidanceReport: (state) => {
      state.published = null;
    },
    /**
     * Begin a run. **Idempotent for the same already-running flow**: re-activating it keeps the
     * original `startedAt` and analytics bookkeeping, so a double-invoked effect cannot reset
     * progress. A completed run of the same flow, or a different flow, starts fresh — there is only
     * ever one record, so starting another flow replaces the current one.
     */
    activateGuide: (state, action: PayloadAction<{ flowId: string; version: number; source: GuideSource; startedAt: number }>) => {
      const current = state.active;
      if (current && current.flowId === action.payload.flowId && current.status === "active") return;

      state.active = { ...action.payload, status: "active", lastEmittedStep: null, lastEmittedBlockedReason: null };
    },
    /**
     * Adopt a record that was already running in this tab (a same-tab refresh). Distinct from
     * `activateGuide` because it is the *same* run continuing: the bookkeeping is preserved and no
     * `guide_started` is emitted for it.
     */
    restoreGuide: (state, action: PayloadAction<ActiveGuide>) => {
      if (state.active) return;

      state.active = action.payload;
    },
    /** The feature reported its terminal state. Only a running guide can complete. */
    markGuideCompleted: (state) => {
      if (!state.active || state.active.status !== "active") return;

      state.active.status = "completed";
    },
    deactivateGuide: (state) => {
      state.active = null;
    },
    recordGuideEmission: (state, action: PayloadAction<{ step: GuidanceStep; blockedReason: GuidanceBlockedReason | null }>) => {
      if (!state.active) return;

      state.active.lastEmittedStep = action.payload.step;
      state.active.lastEmittedBlockedReason = action.payload.blockedReason;
    },
  },
});

export const { setGuidanceReport, clearGuidanceReport, activateGuide, restoreGuide, markGuideCompleted, deactivateGuide, recordGuideEmission } = guidanceSlice.actions;

export default guidanceSlice.reducer;
